import * as THREE from 'three';
import explosionVert from '../../glsl/explosion_sprite_vert.glsl';
import explosionFrag from '../../glsl/explosion_sprite_frag.glsl';
import { EXPLOSION_SPRITES, RENDERING } from '../core/SimConfig.js';

const ANIMATIONS = EXPLOSION_SPRITES.animations;
const EVENTS = EXPLOSION_SPRITES.events;

/**
 * Filmed explosions played back as camera-facing billboards.
 *
 * This is a companion to ParticleSystem, not a replacement: the particles
 * still throw the debris, dust and smoke column, and the billboard adds the
 * fireball's actual shape and roll — the one thing a cloud of puffs can't
 * fake. Every spawner calls both.
 *
 * Each registered clip in SimConfig.EXPLOSION_SPRITES.animations gets its own
 * InstancedMesh (they have different sheets, so they can't share a draw) and
 * its own small pool of pre-allocated playback slots. Live slots are kept
 * packed at the front of the pool and retired by swapping with the last live
 * one, so the per-frame walk is dense and nothing allocates after construction.
 *
 * The billboard is cylindrical — it yaws to face the camera but its up axis
 * stays world-up, so a blast climbing out of the ground never lies down when
 * you overfly it. Facing is derived per-instance in the vertex shader from the
 * camera position, which means update() needs no camera at all.
 */
export default class ExplosionSystem {
  /**
   * Takes the AssetManager rather than a texture, because the set of sheets is
   * whatever SimConfig registers — resolving them here is what keeps adding a
   * clip to config-only.
   *
   * @param {{ scene: THREE.Scene, assets: import('../core/AssetManager.js').default }} opts
   */
  constructor({ scene, assets }) {
    this.scene = scene;

    // Shared unit quad — the vertex shader builds the billboard from its uv.
    this._quadGeo = new THREE.PlaneGeometry(1, 1, 1, 1);

    this._clips = new Map();
    this.spriteMeshes = [];

    for (const [name, cfg] of Object.entries(ANIMATIONS)) {
      const texture = assets?.getTexture(name);
      if (!texture) {
        console.warn(`ExplosionSystem: no sheet loaded for animation '${name}'`);
        continue;
      }
      const clip = this._createClip(cfg, texture);
      this._clips.set(name, clip);
      this.scene.add(clip.mesh);
      this.spriteMeshes.push(clip.mesh);
    }
  }

  // ── Clip construction ──────────────────────────────────────────────────────

  _createClip(cfg, texture) {
    const pool = cfg.pool ?? 4;

    const geo = new THREE.InstancedBufferGeometry();
    geo.index = this._quadGeo.index;
    geo.setAttribute('position', this._quadGeo.getAttribute('position'));
    geo.setAttribute('uv', this._quadGeo.getAttribute('uv'));

    const aOffset = new THREE.InstancedBufferAttribute(new Float32Array(pool * 3), 3);
    const aParams = new THREE.InstancedBufferAttribute(new Float32Array(pool * 4), 4);
    aOffset.setUsage(THREE.DynamicDrawUsage);
    aParams.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aOffset', aOffset);
    geo.setAttribute('aParams', aParams);
    geo.instanceCount = 0;

    // Half a texel of the tile, so bilinear taps at the quad's rim can't reach
    // into the next frame on the sheet.
    const tilePx = (texture.image?.width ?? cfg.cols * 128) / cfg.cols;
    const fire = cfg.fire ?? {};

    const mat = new THREE.RawShaderMaterial({
      uniforms: {
        uSheet:         { value: texture },
        uGrid:          { value: new THREE.Vector2(cfg.cols, cfg.rows) },
        uAnchor:        { value: new THREE.Vector2(cfg.anchor?.[0] ?? 0.5, cfg.anchor?.[1] ?? 0.5) },
        uInset:         { value: 0.5 / tilePx },
        uLogDepthBufFC: { value: 2.0 / (Math.log(RENDERING.farPlane + 1.0) / Math.LN2) },
        uNearFadeStart: { value: EXPLOSION_SPRITES.nearFadeStart },
        uNearFadeEnd:   { value: EXPLOSION_SPRITES.nearFadeEnd },
        uSkyTop:        { value: new THREE.Vector3(1, 1, 1) },
        uSkyBottom:     { value: new THREE.Vector3(0.3, 0.3, 0.3) },
        uEffectBrightness: { value: 1.0 },
        uFire: {
          value: new THREE.Vector4(
            fire.lumLow ?? 0.1,
            fire.lumHigh ?? 0.45,
            fire.warmth ?? 1.5,
            fire.emissive ?? 1.2,
          ),
        },
      },
      glslVersion: THREE.GLSL3,
      vertexShader: `
        precision highp float;
        ${explosionVert}
      `,
      fragmentShader: `
        precision highp float;
        ${explosionFrag}
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneMinusSrcAlphaFactor,
      side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false;

    // Pre-allocated playback slots — slots[0 .. liveCount) are the live ones.
    const slots = [];
    for (let i = 0; i < pool; i++) {
      slots.push({ x: 0, y: 0, z: 0, size: 1, alpha: 1, flip: 1, age: 0 });
    }

    return {
      cfg,
      mesh,
      aOffset,
      aParams,
      slots,
      liveCount: 0,
      duration: cfg.frames / cfg.fps,
    };
  }

  // ── Spawning ───────────────────────────────────────────────────────────────

  /**
   * Play a named clip at a world position.
   *
   * @param {string} name — key in EXPLOSION_SPRITES.animations
   * @param {number} x,y,z — world position of the ignition point (ground level)
   * @param {object} [opts]
   * @param {number} [opts.size=1]  — multiplier on the clip's configured size
   * @param {number} [opts.alpha=1] — overall opacity
   * @returns {boolean} false if the clip is unknown or its pool is full
   */
  spawn(name, x, y, z, opts) {
    const clip = this._clips.get(name);
    if (!clip) return false;
    if (clip.liveCount >= clip.slots.length) return false;

    const s = clip.slots[clip.liveCount++];
    s.x = x;
    s.y = y;
    s.z = z;
    s.size = clip.cfg.size * (opts?.size ?? 1);
    s.alpha = opts?.alpha ?? 1;
    s.flip = clip.cfg.mirror && Math.random() < 0.5 ? -1 : 1;
    s.age = 0;
    return true;
  }

  /**
   * Play whatever clip SimConfig assigns to a game event. Callers name the
   * *event* rather than the clip, so retargeting an event at new footage — or
   * giving it a set of clips to pick from — never touches gameplay code.
   *
   * @param {string} event — key in EXPLOSION_SPRITES.events
   * @param {number} x,y,z — world position of the ignition point
   * @param {object} [opts] — as spawn(); `size` multiplies the event's own
   */
  spawnEvent(event, x, y, z, opts) {
    const e = EVENTS[event];
    if (!e) return false;

    const choices = e.animation;
    const name = Array.isArray(choices)
      ? choices[(Math.random() * choices.length) | 0]
      : choices;

    return this.spawn(name, x, y, z, {
      size: (e.size ?? 1) * (opts?.size ?? 1),
      alpha: opts?.alpha,
    });
  }

  // ── Per-frame update ───────────────────────────────────────────────────────

  /** @param {number} dt — sim seconds (so playback freezes with the pause) */
  update(dt) {
    for (const clip of this._clips.values()) {
      const { slots, cfg, aOffset, aParams } = clip;
      const off = aOffset.array;
      const par = aParams.array;

      let i = 0;
      while (i < clip.liveCount) {
        const s = slots[i];
        s.age += dt;

        const frame = Math.floor(s.age * cfg.fps);
        if (frame >= cfg.frames) {
          // Retire: swap with the last live slot and re-test this index.
          const last = --clip.liveCount;
          if (i !== last) {
            slots[i] = slots[last];
            slots[last] = s;
          }
          continue;
        }

        off[i * 3]     = s.x;
        off[i * 3 + 1] = s.y;
        off[i * 3 + 2] = s.z;
        par[i * 4]     = s.size;
        par[i * 4 + 1] = frame;
        par[i * 4 + 2] = s.flip;
        par[i * 4 + 3] = s.alpha;
        i++;
      }

      clip.mesh.geometry.instanceCount = clip.liveCount;
      if (clip.liveCount > 0) {
        aOffset.needsUpdate = true;
        aParams.needsUpdate = true;
      }
    }
  }

  // ── Lighting ───────────────────────────────────────────────────────────────

  /**
   * Mirror of ParticleSystem.setLighting — the smoke half of a clip is lit by
   * the same hemispheric sky term, so a blast matches the puffs around it.
   * The fire half is unaffected by either value.
   *
   * @param {{ top: number[], bottom: number[] }} skyTint — linear RGB
   * @param {number} [effectBrightness=1] — env-lit dim multiplier
   */
  setLighting(skyTint, effectBrightness = 1) {
    const t = skyTint?.top ?? [1, 1, 1];
    const b = skyTint?.bottom ?? [0.3, 0.3, 0.3];
    for (const clip of this._clips.values()) {
      const u = clip.mesh.material.uniforms;
      u.uSkyTop.value.set(t[0], t[1], t[2]);
      u.uSkyBottom.value.set(b[0], b[1], b[2]);
      u.uEffectBrightness.value = effectBrightness;
    }
  }

  // ── Stats ──────────────────────────────────────────────────────────────────

  get aliveCount() {
    let n = 0;
    for (const clip of this._clips.values()) n += clip.liveCount;
    return n;
  }

  // ── Cleanup ────────────────────────────────────────────────────────────────

  dispose() {
    for (const clip of this._clips.values()) {
      this.scene.remove(clip.mesh);
      clip.mesh.geometry.dispose();
      clip.mesh.material.dispose();
    }
    this._clips.clear();
    this.spriteMeshes.length = 0;
    this._quadGeo.dispose();
    // Sheets belong to AssetManager and are reused by the next scenario —
    // disposing them here would just force a re-upload on the next launch.
  }
}
